import React, { useEffect,  useRef, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardMedia,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import ForecastActions from "./ForecastActions";
import useMediaQuery from "@mui/material/useMediaQuery";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import FavoriteIcon from "@mui/icons-material/Favorite";
import StarIcon from "@mui/icons-material/Star";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ShareIcon from "@mui/icons-material/Share";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BoltIcon from "@mui/icons-material/Bolt";


import { db, auth } from "../../components/firebase";
import { deleteDoc, doc, increment, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { useAuthState } from "react-firebase-hooks/auth";
import useUserProfile from "../hooks/useUserProfile";
import { snapshot } from "viem/actions";

/* ===== SIZES ===== */
const SIZE = {
  title:        { sm: "0.7rem",  md: "0.9rem" },
  metaText:     { sm: "0.72rem", md: "0.82rem" },
  chipHeight:   { sm: 18,        md: 22        },
  mediaHeight:  { sm: 160,       md: 200       },
  cardHeight:   { sm: 320,       md: 380       },
  actionsSlot:  { sm: 36,        md: 40        },
  socialSlot:   { sm: 40,        md: 44        },
};

/* ===== TOKENS ===== */
const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.92) 0%, rgba(10,13,17,.92) 100%)",
  border: "1px solid rgba(255,255,255,.08)",
  shadow: "0 10px 32px rgba(2,8,23,.45)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const ACCENT = {
  PUMP:   { wash: "rgba(16,185,129,.10)",  glow: "rgba(16,185,129,.40)",  chip: { bg: "rgba(0,196,180,.18)",  text: "#93f5ea", border: "1px solid rgba(0,196,180,.36)" } },
  DUMP:   { wash: "rgba(239,68,68,.10)",   glow: "rgba(239,68,68,.40)",   chip: { bg: "rgba(239,83,80,.18)",  text: "#ffb4b1", border: "1px solid rgba(239,83,80,.36)" } },
  FLAT:   { wash: "rgba(234,179,8,.10)",   glow: "rgba(234,179,8,.38)",   chip: { bg: "rgba(255,202,40,.18)", text: "#ffe08a", border: "1px solid rgba(255,202,40,.35)" } },
  RUGPULL:{ wash: "rgba(148,163,184,.10)", glow: "rgba(148,163,184,.28)", chip: { bg: "rgba(255,255,255,.10)",text: "#e5e7eb", border: "1px solid rgba(255,255,255,.16)" } },
};

const CHIP = {
  new: { bg: "rgba(3,169,244,.18)",  text: "#bfe4ff", border: "1px solid rgba(3,169,244,.30)" },
  open:{ bg: "rgba(16,185,129,.18)", text: "#a7f3d0", border: "1px solid rgba(16,185,129,.35)" },
  neutral:{ bg: "rgba(255,255,255,.08)", text: "#e5e7eb", border: "1px solid rgba(255,255,255,.16)" },
};

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => !["$wash", "$glow", "isboosted"].includes(String(prop)),
})(({ $wash, $glow, isboosted }) => ({
  position: "relative",
  borderRadius: 16,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  border: GLASS.border,
  background: GLASS.bg,
  color: GLASS.text,
  boxShadow: GLASS.shadow,
  transition: "transform .2s, box-shadow .2s, border-color .2s, filter .2s",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background: `radial-gradient(90% 60% at 15% -10%, ${$wash} 0%, rgba(0,0,0,0) 60%),
                 radial-gradient(70% 50% at 110% 0%, ${$wash} 0%, rgba(0,0,0,0) 65%)`,
    opacity: 1,
  },
  boxShadow: `0 10px 32px rgba(2,8,23,.45), 0 0 0 1px ${$wash}`,
  "&:hover": {
    transform: "translateY(-4px)",
    borderColor: "rgba(255,255,255,.18)",
    boxShadow: `0 16px 40px rgba(2,8,23,.55), 0 0 0 1px ${$glow}, 0 0 36px ${$glow}`,
    filter: "saturate(1.05)",
  },
  "@keyframes pulse": {
    "0%": { boxShadow: "0 0 0 0 rgba(20,241,149,.45)" },
    "70%": { boxShadow: "0 0 0 14px rgba(20,241,149,0)" },
    "100%": { boxShadow: "0 0 0 0 rgba(20,241,149,0)" },
  },
  animation: isboosted ? "pulse 2.2s infinite" : "none",
}));

const CardTopChips = styled(Box)(() => ({
  position: "absolute",
  top: 8,
  left: 8,
  right: 8,
  display: "flex",
  flexWrap: "wrap",
  columnGap: 6,
  rowGap: 6,
  zIndex: 3,
  pointerEvents: "auto",
}));

const MediaOverlay = styled(Box)(() => ({
  position: "absolute",
  inset: 0,
  zIndex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  background: "linear-gradient(180deg, rgba(0,0,0,.35) 0%, rgba(0,0,0,.0) 40%, rgba(0,0,0,.45) 100%)",
  pointerEvents: "none",
}));

const BottomBadges = styled(Box)(() => ({
  display: "flex",
  gap: 6,
  padding: 8,
  alignItems: "center",
  justifyContent: "space-between",
}));

const formatUSD = (n, digits = 2) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  const num = Number(n);
  const abs = Math.abs(num);
  if (abs >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toFixed(digits);
};

const ForecastCardForecast = ({ task }) => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const theme = useTheme();
  const downSm = useMediaQuery(theme.breakpoints.down("sm"));
  const compact = downSm;

  const fsTitle = downSm ? SIZE.title.sm : SIZE.title.md;
  const fsMeta  = downSm ? SIZE.metaText.sm : SIZE.metaText.md;
  const chipH   = downSm ? SIZE.chipHeight.sm : SIZE.chipHeight.md;
  const imgH    = downSm ? SIZE.mediaHeight.sm : SIZE.mediaHeight.md;
  const cardH   = downSm ? SIZE.cardHeight.sm  : SIZE.cardHeight.md;
  const actH    = downSm ? SIZE.actionsSlot.sm : SIZE.actionsSlot.md;
  const socialH = downSm ? SIZE.socialSlot.sm  : SIZE.socialSlot.md;
  const actionsRef = useRef(null);
  const [liked, setLiked] = useState(false);
  const [favorited, setFavorited] = useState(false);
  const [likes, setLikes] = useState(task?.likes || 0);
  const [views, setViews] = useState(task?.views || 0);
  const { profile } = useUserProfile(task?.userId);
  const currency = (task?.token || 'prUSD').toUpperCase();

  const prediction = (task?.predictionType || "").toUpperCase();
  const progress = String(task?.progressStatus || "NEW").toUpperCase();
  const predStyle = (ACCENT[prediction] || ACCENT.RUGPULL).chip;
  const progStyle = progress === "NEW" ? CHIP.new : progress === "OPEN" ? CHIP.open : CHIP.neutral;

  useEffect(() => {
    if (!user || !task?.id) return;
    const unsubLikes = onSnapshot(doc(db, `users/${user.uid}/likes`, task.id), (d) => setLiked(d.exists()));
    const unsubFavs  = onSnapshot(doc(db, `users/${user.uid}/favorites`, task.id), (d) => setFavorited(d.exists()));
    const unsubPromo = onSnapshot(doc(db, "promos", task.id), (d) => {
      if (!d.exists()) return;
      const data = d.data() || {};
      setLikes(Number(data.likes || 0));
      setViews(Number(data.views || 0));
    });
    return () => { unsubLikes(); unsubFavs(); unsubPromo(); toast.dismiss(); };
  }, [user, task?.id]);

  const goToDetails = async (e) => {
    e?.stopPropagation?.();
    try {
      if (user?.uid && task?.userId && user.uid === task.userId && task?.id) {
        await updateDoc(doc(db, "promos", task.id), { views: increment(1) });
      }
    } catch {}
    navigate(`/forecast/${task?.id || ""}`);
  };

  const toggleLike = async () => {
    if (!user) return toast.error("Log in to like");
    try {
      const likeRef = doc(db, `users/${user.uid}/likes`, task.id);
      if (liked) await deleteDoc(likeRef); else await setDoc(likeRef, { t: Date.now() });
      setLiked(!liked);
    } catch { toast.error("Failed to update like"); }
  };
  const toggleFav = async () => {
  if (!user) return toast.error("Log in to add favorites");
  try {
    const favRef = doc(db, `users/${user.uid}/favorites`, task.id);
    if (favorited) {
      await deleteDoc(favRef);
      setFavorited(false);
      toast.success("Removed from favorites");
    } else {
      // минимальный снапшот, который нужен в таблице избранного
      const snapshot = {
        id: task.id,
        title: task.title || "",
        assets: Array.isArray(task.assets) ? task.assets : [],
        predictionType: task.predictionType || "",
        pool: task.pool ?? 0,
        due: task.due || "",
        status: task.status || "",
      };
      await setDoc(
        favRef,
        { promoId: task.id, task: snapshot, t: Date.now() },
        { merge: true }
      );
      setFavorited(true);
      toast.success("Added to favorites");
    }
  } catch {
    toast.error("Failed to update favorite");
  }
};


  const onStake = (e) => { e.stopPropagation(); toast.info("Stake feature coming soon!"); };
  const onBoost = (e) => { e.stopPropagation(); toast.info("Boost feature coming soon!"); };
  const onTip   = (e) => { e.stopPropagation(); toast.info("Tip feature coming soon!"); };

  const accent = ACCENT[prediction] || ACCENT.RUGPULL;
  const creatorName = profile?.displayName || task?.userDisplayName || task?.companyName || task?.creator || "Unknown";
  const creatorLevel = task?.creatorLevel || "Beginner";

  // top-chips sizing
  const topChip = { h: downSm ? 16 : 20, fs: downSm ? "0.58rem" : "0.70rem", icon: downSm ? 13 : 15, labelPx: downSm ? 0.5 : 0.7 };
  const topChipBaseSx = {
    height: topChip.h, fontSize: topChip.fs, fontWeight: 800,
    "& .MuiChip-icon": { fontSize: topChip.icon, mr: 0.4 },
    "& .MuiChip-label": { px: topChip.labelPx },
  };

  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}>
      <StyledCard
        isboosted={task?.boostStatus?.active ? "true" : undefined}
        $wash={accent.wash}
        $glow={accent.glow}
          sx={{
    height: { xs: "auto", sm: cardH },   
    position: "relative",
    display: "flex",
    flexDirection: "column"
  }}
  >
        {/* TOP CHIPS */}
        <CardTopChips>
          {!!prediction && (
            <Chip size="small" label={prediction}
              sx={{ ...topChipBaseSx, bgcolor: predStyle.bg, color: predStyle.text, border: predStyle.border }} />
          )}
          {!!progress && (
            <Chip size="small" label={progress}
              sx={{ ...topChipBaseSx, bgcolor: progStyle.bg, color: progStyle.text, border: progStyle.border }} />
          )}
          {task?.boostStatus?.active && (
            <Chip size="small" icon={<BoltIcon sx={{ fontSize: topChip.icon }} />} label="BOOSTED"
              sx={{ ...topChipBaseSx, bgcolor: "rgba(20,241,149,.14)", color: "#bfffe5", border: "1px solid rgba(20,241,149,.32)" }} />
          )}
        </CardTopChips>

        <CardActionArea
          onClick={goToDetails}
          role="button"
          tabIndex={0}
          sx={{ outline: "none", display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}
        >
          {/* MEDIA */}
          <Box sx={{ position: "relative", width: "100%" }}>
            <CardMedia
              component="img"
              image={task?.images?.[0] || task?.avatar || "https://placehold.co/600x400"}
              alt={task?.title || "No title"}
              sx={{ height: imgH, objectFit: "cover", borderBottom: `1px solid ${accent.wash}` }}
              onError={(e) => { e.currentTarget.src = "https://placehold.co/600x400"; }}
            />
            <MediaOverlay sx={{ justifyContent: "flex-end" }}>
              <BottomBadges>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, pointerEvents: "auto", minWidth: 0, flex: 1, maxWidth: "72%" }}>
                  <Avatar src={profile?.photoURL || task?.avatar} sx={{ width: { xs: 26, sm: 28 }, height: { xs: 26, sm: 28 }, flexShrink: 0 }} />
                  <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      title={creatorName}
                      sx={{ color: "#e5e7eb", fontWeight: 700, fontSize: { xs: "0.58rem", sm: "0.78rem" }, lineHeight: 1.1, maxWidth: "100%", minWidth: 0 }}
                    >
                      {creatorName}
                    </Typography>
                    <Chip
                      size="small"
                      label={creatorLevel}
                      sx={{
                        mt: 0.25, alignSelf: "flex-start",
                        height: { xs: 12, sm: 16 }, fontSize: { xs: "0.48rem", sm: "0.60rem" }, lineHeight: 1,
                        bgcolor: "rgba(255,255,255,.10)", color: GLASS.text, border: "1px solid rgba(255,255,255,.16)",
                        "& .MuiChip-label": { px: { xs: 0.35, sm: 0.5 } },
                      }}
                    />
                  </Box>
                </Box>
                {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, pointerEvents: "auto", flexShrink: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <VisibilityIcon sx={{ color: "#cbd5e1", fontSize: { xs: 16, sm: 18 } }} />
                    <Typography variant="caption" sx={{ color: GLASS.text, fontWeight: 700, fontSize: { xs: "0.68rem", sm: "0.72rem" } }}>
                      {views}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                    <FavoriteIcon sx={{ color: liked ? "#fda4af" : "#cbd5e1", fontSize: { xs: 16, sm: 18 } }} />
                    <Typography variant="caption" sx={{ color: GLASS.text, fontWeight: 700, fontSize: { xs: "0.68rem", sm: "0.72rem" } }}>
                      {likes}
                    </Typography>
                  </Box>
                </Box> */}
              </BottomBadges>
            </MediaOverlay>
          </Box>

          {/* BODY */}
          <Box sx={{ p: downSm ? 1.1 : 1.5, display: "flex", flexDirection: "column", gap: downSm ? 0.6 : 0.75, flex: 1, minHeight: 0, minWidth: 0 }}>
            {/* Title — 2 строки, потом … */}
            <Typography
              component="h3"
              title={task?.title || "Untitled"}
              sx={{
                fontWeight: 800, color: GLASS.text, lineHeight: 1.15, fontSize: fsTitle,
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", minWidth: 0, maxWidth: "100%",
              }}
            >
              {task?.title || "Untitled"}
            </Typography>

            {/* META: due + pool */}

<Box sx={{ display: "grid", gridTemplateColumns: downSm ? "1fr" : "1fr 1fr", gap: 0.8, minWidth: 0 }}>
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
    <CalendarTodayIcon sx={{ color: GLASS.subtext, fontSize: downSm ? 16 : 19, flexShrink: 0 }} />
    <Typography
      sx={{
        color: GLASS.subtext,
        fontSize: downSm ? "0.64rem" : fsMeta,  // ⟵ меньше на мобиле
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0
      }}
    >
      {task?.due || "N/A"}
    </Typography>
  </Box>

  <Box sx={{ display: "flex", justifyContent: downSm ? "flex-start" : "flex-end", minWidth: 0 }}>
    <Chip
      size="small"
      variant="outlined"
      icon={!downSm ? <RocketLaunchIcon sx={{ fontSize: 16 }} /> : undefined}
      label={`Pool ${formatUSD(task?.pool || 0, 0)} ${currency}`}
      sx={{
        height: downSm ? 16 : chipH,                    // ⟵ ниже чип на мобиле
        "& .MuiChip-label": { px: downSm ? 0.4 : 0.8 }, // ⟵ компактнее паддинги
        fontSize: downSm ? "0.58rem" : { xs: "0.62rem", sm: "0.70rem" },
        color: "#a7f3d0",
        borderColor: alpha("#16a34a", 0.35),
      }}
    />
  </Box>
</Box>


          </Box>
        </CardActionArea>


{/* ACTION*/}
  {task?.isForecast && (
  <Box
    sx={{
      px: compact ? 1 : 1.5,
      py: 0.6,
      width: '100%',
      display: 'flex',
      justifyContent: 'center',              // центруем чипы
      borderTop: '1px solid rgba(255,255,255,.08)', // визуальный разделитель
    }}
  >
    <ForecastActions
      ref={actionsRef}
      forecastId={task.id}
      creatorId={task.userId}
      compact={compact}
      currency={currency}
    />
  </Box>
)}


{/* SOCIAL — отдельная полоса, жёстко ниже actions */}
<Box
  sx={{
    px: compact ? 1 : 1.5,
    py: 0.6,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",     // ⟵ разделяем левую/правую группы
    gap: 1,
    borderTop: "1px solid rgba(255,255,255,.08)",   // ⟵ второй разделитель — визуально читаемо
    mt: 0,                               // никаких отрицательных отступов
    position: "relative",
    zIndex: 0,
    minHeight: socialH,
  }}
>
  {/* left: views + likes */}
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <VisibilityIcon sx={{ color: "#cbd5e1", fontSize: { xs: 16, sm: 18 } }} />
      <Typography variant="caption" sx={{ color: "#e5e7eb", fontWeight: 700 }}>
        {views}
      </Typography>
    </Box>
    <Tooltip title={liked ? "Unlike" : "Like"}>
      <IconButton
        onClick={(e)=>{ e.stopPropagation(); toggleLike(); }}
        size="small"
        sx={{ "&:hover": { backgroundColor: "transparent" }, ml: 0.25 }}
      >
        <FavoriteIcon sx={{ color: liked ? "#fda4af" : "#cbd5e1", fontSize: { xs: 16, sm: 18 } }} />
      </IconButton>
    </Tooltip>
    <Typography variant="caption" sx={{ color: "#e5e7eb", fontWeight: 700 }}>
      {likes}
    </Typography>
  </Box>

  
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    <Tooltip title={favorited ? "Remove from Favorites" : "Add to Favorites"}>
      <IconButton
        onClick={(e)=>{ e.stopPropagation(); toggleFav(); }}
        size="small"
        sx={{ "&:hover": { backgroundColor: "transparent" } }}
      >
        <StarIcon sx={{ color: favorited ? "#fde68a" : "#cbd5e1", fontSize: { xs: 16, sm: 18 } }} />
      </IconButton>
    </Tooltip>
    <Tooltip title="Share">
      <IconButton
        onClick={(e)=>{ e.stopPropagation(); snapshot(); }}
        size="small"
        sx={{ "&:hover": { backgroundColor: "transparent" } }}
      >
        <ShareIcon sx={{ color: "#cbd5e1", fontSize: { xs: 16, sm: 18 } }} />
      </IconButton>
    </Tooltip>
  </Box>
</Box>

      </StyledCard>
    </motion.div>
  );
};

export default ForecastCardForecast;
